<!-- NotesApp Source File: calendar.component.html -->

<div class="cal-root">

  <!-- ═══════════════════════════════════════════════════════
       HEADER  ─  title · month/year · navigation
       ═══════════════════════════════════════════════════════ -->
  <div class="cal-header">

    <!-- Left: current month label (the app header already names the view) -->
    <div class="cal-header-left">
      <span class="cal-month-label">{{ monthLabel }}</span>
    </div>

    <div class="cal-visibility-toggle" role="group" aria-label="Calendar note visibility">
      <button
        type="button"
        class="cal-toggle-btn"
        [class.cal-toggle-btn--active]="visibilityMode === 'calendar'"
        (click)="setVisibilityMode('calendar')"
        title="Show only notes tagged as Calendar">
        Calendar Only
      </button>
      <button
        type="button"
        class="cal-toggle-btn"
        [class.cal-toggle-btn--active]="visibilityMode === 'all'"
        (click)="setVisibilityMode('all')"
        title="Show all notes that match current filters">
        All Notes
      </button>
    </div>

    <!-- Right: navigation controls -->
    <div class="cal-header-nav">

      <button class="cal-nav-btn" title="Previous month" (click)="navigatePrev()">
        <i class="pi pi-chevron-left"></i>
        <span>Previous</span>
      </button>

      <button
        class="cal-nav-btn cal-nav-btn--today"
        [class.cal-nav-btn--today-disabled]="isShowingCurrentMonth"
        [disabled]="isShowingCurrentMonth"
        (click)="goToToday()">
        Today
      </button>

      <div class="cal-nav-divider" aria-hidden="true"></div>

      <button class="cal-nav-btn cal-nav-btn--goto" (click)="openGoToDialog()">
        <i class="pi pi-calendar"></i>
        <span>Go to&hellip;</span>
      </button>

      <button class="cal-nav-btn" title="Next month" (click)="navigateNext()">
        <span>Next</span>
        <i class="pi pi-chevron-right"></i>
      </button>

    </div>
  </div>

  <!-- ═══════════════════════════════════════════════════════
       CALENDAR GRID
       ═══════════════════════════════════════════════════════ -->
  <div class="cal-grid-wrapper">
    <table class="cal-table" role="grid" [attr.aria-label]="monthLabel">

      <!-- Day-of-week column headers -->
      <thead>
        <tr>
          @for (name of dayNamesLong; track name; let i = $index) {
            <th class="cal-col-header" [class.cal-col-header--weekend]="i >= 5" scope="col">{{ name }}</th>
          }
        </tr>
      </thead>

      <!-- Week rows -->
      <tbody>
        @for (week of weeks; track $index) {
          <tr>
            @for (day of week.days; track day.date.getTime()) {
              <td
                class="cal-cell"
                [class.cal-cell--other-month]="!day.isCurrentMonth"
                [class.cal-cell--today]="day.isToday"
                [class.cal-cell--weekend]="day.isWeekend"
                [attr.aria-label]="day.date | date:'EEEE, MMMM d, yyyy'"
                [attr.aria-current]="day.isToday ? 'date' : null">

                <div class="cal-cell-inner">
                  <div class="cal-day-header">
                    <span
                      class="cal-day-num"
                      [class.cal-day-num--today]="day.isToday">
                      {{ day.dayNumber }}
                    </span>
                  </div>
                  <div class="cal-notes" role="list" [attr.aria-label]="'Notes for ' + (day.date | date:'fullDate')">
                    @for (note of getNotesForDay(day.date); track trackByNoteId($index, note)) {
                      <a
                        role="listitem"
                        class="cal-note"
                        href="#"
                        (click)="openNoteFromCalendar(note, $event)"
                        [title]="noteHoverText(note)"
                        [ngClass]="{
                          'font-bold': isEmphasizedStatus(note.Status),
                          'italic text-[var(--textColorFaded)]': isInactiveForDisplay(note),
                          '!line-through text-red-600': note.syncStatus === 'deleted',
                          '!text-red-500': note.syncStatus !== 'synced' && note.syncStatus !== 'deleted'
                        }">
                        {{ noteTitle(note) }}
                      </a>
                    }
                  </div>
                  <button
                    class="cal-add-btn"
                    title="New note for this day"
                    [disabled]="creatingNoteForDay !== null"
                    (click)="createNoteForDay(day.date, $event)">
                    <i class="pi pi-plus"></i>
                  </button>
                </div>

              </td>
            }
          </tr>
        }
      </tbody>

    </table>
  </div>

</div>


<!-- ═══════════════════════════════════════════════════════
     "GO TO MONTH" DIALOG
     ═══════════════════════════════════════════════════════ -->
<p-dialog
  header="Go to Month"
  [(visible)]="goToDialogVisible"
  [modal]="true"
  [draggable]="false"
  [resizable]="false"
  [style]="{ width: '22rem', maxWidth: '95vw' }"
  appendTo="body"
  (onHide)="cancelGoTo()">

  <div class="flex justify-center py-2">
    <p-datepicker
      [(ngModel)]="goToDate"
      view="month"
      dateFormat="MM yy"
      [inline]="true"
      [showButtonBar]="false">
    </p-datepicker>
  </div>

  <ng-template pTemplate="footer">
    <div class="flex justify-end gap-2">
      <p-button label="Cancel" severity="secondary" (onClick)="cancelGoTo()"></p-button>
      <p-button label="Go" icon="pi pi-arrow-right" iconPos="right" (onClick)="applyGoTo()"></p-button>
    </div>
  </ng-template>

</p-dialog>
